import { useState, useEffect, useRef, useCallback } from "react";
import { createFileRoute } from "@tanstack/react-router";
import {
  FileCode,
  Loader2,
  CheckCircle2,
  XCircle,
  ExternalLink,
  Play,
  Clock,
  AlertTriangle,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { toast } from "sonner";
import { useAuth } from "@/contexts/AuthContext";
import { portalFetch } from "@/lib/portalApi";

export const Route = createFileRoute("/portal/platform/sdlc")({
  component: SdlcPage,
});

// ---------------------------------------------------------------------------
// Agent type registry — add new agent types here
// ---------------------------------------------------------------------------

interface AgentTypeConfig {
  label: string;
  description: string;
  taskType: string;
  fields: FieldConfig[];
}

interface FieldConfig {
  name: string;
  label: string;
  type: "text" | "tags";
  required?: boolean;
  placeholder?: string;
  description?: string;
}

const AGENT_TYPES: Record<string, AgentTypeConfig> = {
  connector_factory: {
    label: "B2C Connector",
    description:
      "Generate a production-ready Business2Cloud HTTP source connector with C# code, SQL configuration, and a GitHub PR.",
    taskType: "create_connector",
    fields: [
      {
        name: "target_api",
        label: "Target API",
        type: "text",
        required: true,
        placeholder: "e.g. Shopify, Stripe, HubSpot",
        description: "The API provider to generate a connector for.",
      },
      {
        name: "record_types",
        label: "Record Types",
        type: "tags",
        required: true,
        placeholder: "e.g. Orders, Products, Customers",
        description:
          "Comma-separated list of entity types to sync from the API.",
      },
      {
        name: "api_docs_url",
        label: "API Docs URL",
        type: "text",
        placeholder: "https://docs.example.com/api",
        description: "Link to the API documentation (optional but recommended).",
      },
      {
        name: "auth_type",
        label: "Auth Type Hint",
        type: "text",
        placeholder: "e.g. Bearer, ApiKey, OAuth2",
        description: "Hint for the authentication strategy (optional).",
      },
      {
        name: "pagination_type",
        label: "Pagination Type Hint",
        type: "text",
        placeholder: "e.g. Cursor, PageLimit, ODataNextLink",
        description: "Hint for the pagination strategy (optional).",
      },
    ],
  },
};

// ---------------------------------------------------------------------------
// Job tracking
// ---------------------------------------------------------------------------

interface TrackedJob {
  jobId: string;
  agentType: string;
  label: string;
  taskData: Record<string, unknown>;
  status: "pending" | "running" | "completed" | "failed";
  result?: Record<string, unknown>;
  error?: string;
  startedAt: string;
}

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

function SdlcPage() {
  const { token } = useAuth();
  const [selectedAgent, setSelectedAgent] = useState<string>("connector_factory");
  const [formData, setFormData] = useState<Record<string, string>>({});
  const [submitting, setSubmitting] = useState(false);
  const [jobs, setJobs] = useState<TrackedJob[]>([]);
  const pollTimers = useRef<Record<string, ReturnType<typeof setInterval>>>({});

  // Clean up poll timers on unmount
  useEffect(() => {
    const timers = pollTimers.current;
    return () => {
      for (const timer of Object.values(timers)) {
        clearInterval(timer);
      }
    };
  }, []);

  const config = AGENT_TYPES[selectedAgent];

  // Reset form when agent type changes
  const handleAgentChange = useCallback((value: string) => {
    setSelectedAgent(value);
    setFormData({});
  }, []);

  // Build task_data from form
  function buildTaskData(): Record<string, unknown> {
    const data: Record<string, unknown> = {};
    for (const field of config.fields) {
      const value = formData[field.name] ?? "";
      if (field.type === "tags") {
        data[field.name] = value
          .split(",")
          .map((s) => s.trim())
          .filter(Boolean);
      } else {
        data[field.name] = value;
      }
    }
    return data;
  }

  // Validate required fields
  function validate(): string | null {
    for (const field of config.fields) {
      if (!field.required) continue;
      const value = formData[field.name] ?? "";
      if (!value.trim()) return `${field.label} is required`;
      if (field.type === "tags") {
        const items = value
          .split(",")
          .map((s) => s.trim())
          .filter(Boolean);
        if (items.length === 0) return `${field.label} must have at least one item`;
      }
    }
    return null;
  }

  // Poll for job status
  const startPolling = useCallback(
    (jobId: string) => {
      if (pollTimers.current[jobId]) return;

      const timer = setInterval(async () => {
        try {
          const data = await portalFetch<Record<string, unknown>>(
            `/api/v1/platform/sdlc/jobs/${jobId}/status`,
            { token: token ?? "" },
          );

          const status = data.status as string;

          setJobs((prev) =>
            prev.map((j) => {
              if (j.jobId !== jobId) return j;
              if (status === "completed") {
                clearInterval(pollTimers.current[jobId]);
                delete pollTimers.current[jobId];
                return {
                  ...j,
                  status: "completed",
                  result: (data.result as Record<string, unknown>) ?? data,
                };
              }
              if (status === "failed") {
                clearInterval(pollTimers.current[jobId]);
                delete pollTimers.current[jobId];
                return {
                  ...j,
                  status: "failed",
                  error: (data.error as string) ?? "Unknown error",
                };
              }
              return { ...j, status: "running" };
            }),
          );
        } catch {
          // Silently retry on next interval
        }
      }, 5000);

      pollTimers.current[jobId] = timer;
    },
    [token],
  );

  // Submit job
  async function handleSubmit() {
    const error = validate();
    if (error) {
      toast.error(error);
      return;
    }

    setSubmitting(true);
    try {
      const taskData = buildTaskData();

      const resp = await portalFetch<{ job_id: string; status_url: string }>(
        "/api/v1/platform/sdlc/jobs",
        {
          token: token ?? "",
          method: "POST",
          body: JSON.stringify({
            agent_type: selectedAgent,
            task_type: config.taskType,
            task_data: taskData,
          }),
        },
      );

      const job: TrackedJob = {
        jobId: resp.job_id,
        agentType: selectedAgent,
        label: config.label,
        taskData,
        status: "pending",
        startedAt: new Date().toISOString(),
      };

      setJobs((prev) => [job, ...prev]);
      startPolling(resp.job_id);
      setFormData({});
      toast.success(`Job submitted — ${resp.job_id.slice(0, 8)}...`);
    } catch (err) {
      toast.error(
        err instanceof Error ? err.message : "Failed to submit job",
      );
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div>
        <h1 className="text-2xl font-semibold tracking-tight flex items-center gap-2">
          <FileCode className="h-6 w-6" />
          Code Generation
        </h1>
        <p className="text-muted-foreground mt-1">
          Trigger SDLC agents to generate code, create PRs, and validate builds.
        </p>
      </div>

      {/* New Job Form */}
      <Card>
        <CardHeader>
          <CardTitle>New Job</CardTitle>
          <CardDescription>
            Select an agent type and fill in the parameters.
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          {/* Agent type selector */}
          <div className="space-y-2">
            <Label>Agent Type</Label>
            <Select value={selectedAgent} onValueChange={handleAgentChange}>
              <SelectTrigger className="w-full max-w-sm">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {Object.entries(AGENT_TYPES).map(([key, cfg]) => (
                  <SelectItem key={key} value={key}>
                    {cfg.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            <p className="text-sm text-muted-foreground">{config.description}</p>
          </div>

          {/* Dynamic fields */}
          {config.fields.map((field) => (
            <div key={field.name} className="space-y-1 max-w-lg">
              <Label>
                {field.label}
                {field.required && <span className="text-destructive ml-0.5">*</span>}
              </Label>
              <Input
                value={formData[field.name] ?? ""}
                onChange={(e) =>
                  setFormData((prev) => ({
                    ...prev,
                    [field.name]: e.target.value,
                  }))
                }
                placeholder={field.placeholder}
              />
              {field.description && (
                <p className="text-xs text-muted-foreground">{field.description}</p>
              )}
            </div>
          ))}

          <Button onClick={handleSubmit} disabled={submitting} className="mt-2">
            {submitting ? (
              <>
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                Submitting...
              </>
            ) : (
              <>
                <Play className="mr-2 h-4 w-4" />
                Run Agent
              </>
            )}
          </Button>
        </CardContent>
      </Card>

      {/* Jobs List */}
      {jobs.length > 0 && (
        <Card>
          <CardHeader>
            <CardTitle>Jobs</CardTitle>
            <CardDescription>
              Jobs are polled every 5 seconds. Results appear once complete.
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            {jobs.map((job) => (
              <JobCard key={job.jobId} job={job} />
            ))}
          </CardContent>
        </Card>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Job Card
// ---------------------------------------------------------------------------

function JobCard({ job }: { job: TrackedJob }) {
  const statusConfig = {
    pending: { icon: Clock, label: "Pending", variant: "secondary" as const },
    running: { icon: Loader2, label: "Running", variant: "default" as const },
    completed: {
      icon: CheckCircle2,
      label: "Completed",
      variant: "default" as const,
    },
    failed: {
      icon: XCircle,
      label: "Failed",
      variant: "destructive" as const,
    },
  };

  const { icon: StatusIcon, label: statusLabel, variant } =
    statusConfig[job.status];

  const prUrl = job.result?.pr_url as string | undefined;
  const buildSucceeded = job.result?.compilation_succeeded as boolean | undefined;
  const confidence = job.result?.confidence as number | undefined;
  const branchName = job.result?.branch_name as string | undefined;

  return (
    <div className="rounded-lg border p-4 space-y-2">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <Badge variant={variant} className="flex items-center gap-1">
            <StatusIcon
              className={`h-3 w-3 ${job.status === "running" ? "animate-spin" : ""}`}
            />
            {statusLabel}
          </Badge>
          <span className="font-medium">{job.label}</span>
          <span className="text-xs text-muted-foreground font-mono">
            {job.jobId.slice(0, 8)}
          </span>
        </div>
        <span className="text-xs text-muted-foreground">
          {new Date(job.startedAt).toLocaleTimeString()}
        </span>
      </div>

      {/* Task summary */}
      <div className="text-sm text-muted-foreground">
        {Object.entries(job.taskData).map(([key, value]) => {
          const display = Array.isArray(value)
            ? (value as string[]).join(", ")
            : String(value);
          if (!display) return null;
          return (
            <span key={key} className="mr-4">
              <span className="font-medium">{key}:</span> {display}
            </span>
          );
        })}
      </div>

      {/* Result details */}
      {job.status === "completed" && job.result && (
        <div className="flex items-center gap-4 text-sm pt-1">
          {buildSucceeded !== undefined && (
            <span className="flex items-center gap-1">
              {buildSucceeded ? (
                <CheckCircle2 className="h-4 w-4 text-green-500" />
              ) : (
                <AlertTriangle className="h-4 w-4 text-amber-500" />
              )}
              Build {buildSucceeded ? "passed" : "failed"}
            </span>
          )}
          {confidence !== undefined && (
            <span>
              Confidence: <strong>{Math.round(confidence * 100)}%</strong>
            </span>
          )}
          {branchName && (
            <span className="font-mono text-xs">{branchName}</span>
          )}
          {prUrl && (
            <a
              href={prUrl}
              target="_blank"
              rel="noopener noreferrer"
              className="flex items-center gap-1 text-primary hover:underline"
            >
              <ExternalLink className="h-3 w-3" />
              View PR
            </a>
          )}
        </div>
      )}

      {/* Error */}
      {job.status === "failed" && job.error && (
        <p className="text-sm text-destructive">{job.error}</p>
      )}
    </div>
  );
}
